import { defineStore } from 'pinia'

export type ToastStatus = 'success' | 'warning' | 'working' | 'error'

interface Toast {
	text: string
	status: ToastStatus
	id: number
	dismissable: boolean
	action: Action | undefined
}

interface Action {
	text: string
	method: Function
}

type ToastPayload = {
	timeout?: number
	text: string
	dismissable?: boolean
	action?: Action | undefined
}
const defaultTimeout = 6000

const createToast = (
	text: string,
	status: ToastStatus,
	dismissable: boolean,
	action: Action | undefined
): Toast => ({
	text,
	status,
	id: Math.random() * 1000,
	dismissable,
	action
})

export default defineStore('toaster', {
	state: (): { toasts: Toast[] } => ({
		toasts: []
	}),
	actions: {
		updateState(payload: ToastPayload, status: ToastStatus) {
			// Get text and timeout from payload
			const { text, timeout, dismissable = true, action } = payload

			// Create the toast
			const toast = createToast(text, status, dismissable, action)

			// Push to the state
			this.toasts.push(toast)

			// Timer
			if (timeout === undefined || timeout > 0) {
				setTimeout(() => {
					this.toasts = this.toasts.filter((t) => t.id !== toast.id)
				}, timeout ?? defaultTimeout)
			}
		},

		dismiss(id: number) {
			this.toasts = this.toasts.filter((t) => t.id !== id)
		},

		success(payload: ToastPayload) {
			this.updateState(payload, 'success')
		},

		warning(payload: ToastPayload) {
			this.updateState(payload, 'warning')
		},

		working(payload: ToastPayload) {
			this.updateState(payload, 'working')
		},

		error(payload: ToastPayload) {
			this.updateState(payload, 'error')
		},

		clear() {
			this.toasts = []
		}
	}
})
